import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { api, imgSrc } from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="pt-44 pb-24 container-site text-brand-charcoal/50 text-sm">Loading article…</div>;
  }

  if (!post) {
    return (
      <div className="pt-44 pb-24 container-site">
        <h1 className="font-display text-3xl font-extrabold text-brand-deep">Article not found</h1>
        <Link to="/knowledge" data-testid="article-not-found-back" className="mt-4 inline-block text-brand-gold font-bold">
          ← Back to Farm Knowledge
        </Link>
      </div>
    );
  }

  const blocks = (post.content || "").split("\n\n").filter(Boolean);

  return (
    <>
      <Seo title={`${post.title} | ICROPSIA Farm Knowledge`} description={post.excerpt} />
      <section className="pt-32 sm:pt-40 pb-10">
        <div className="container-site max-w-3xl">
          <nav className="flex items-center gap-1.5 text-xs font-semibold text-brand-deep/50 mb-7" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-deep transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/knowledge" className="hover:text-brand-deep transition-colors">Farm Knowledge</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-brand-deep truncate max-w-[180px]">{post.title}</span>
          </nav>
          <Reveal>
            <Eyebrow>{post.category}</Eyebrow>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.9rem] font-extrabold tracking-tight text-brand-deep leading-[1.1]">
              {post.title}
            </h1>
          </Reveal>
        </div>
      </section>

      {post.cover_image && (
        <div className="container-site max-w-4xl">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden aspect-[16/8] shadow-2xl shadow-brand-deep/15">
              <img src={imgSrc(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      )}

      <article data-testid="blog-article-body" className="container-site max-w-3xl py-12 pb-20">
        {blocks.map((block, i) =>
          block.startsWith("## ") ? (
            <h2 key={i} className="font-display text-2xl font-extrabold text-brand-deep tracking-tight mt-10 mb-3">
              {block.replace("## ", "")}
            </h2>
          ) : (
            <p key={i} className="text-brand-charcoal/75 leading-relaxed text-base sm:text-lg mb-5">
              {block}
            </p>
          )
        )}
        <div className="mt-14 rounded-3xl bg-brand-deep text-white p-8 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-20" />
          <div className="relative">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Explore the ICROPSIA Portfolio</h2>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">
              Premium maize and wheat seed varieties in professional 25 kg packaging, available across Bihar.
            </p>
            <Link
              to="/products"
              data-testid="article-products-cta"
              className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-bold text-brand-deep transition-all hover:bg-brand-wheat"
            >
              View Our Seeds <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
